import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { 
  Plus, Edit2, Trash2, Eye, Sparkles, LogOut, 
  FileText, Clock, CheckCircle, Loader2, Upload,
  Image, Globe, X, Link as LinkIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { VoiceInput } from '../../components/ui/VoiceInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdmin = ({ skipAuth = false }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(skipAuth || !!localStorage.getItem('adminToken'));
  const [loginEmail, setLoginEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingStatus, setGeneratingStatus] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categories, setCategories] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // null | 'new' | slug
  const [catForm, setCatForm] = useState({ name: '', name_en: '' });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    meta_description: '',
    excerpt: '',
    content: '',
    category: '',
    category_name: '',
    tags: '',
    featured_image: '',
    reading_time: 5,
    status: 'draft',
    faq: []
  });

  // AI generation form - Extended
  const [aiKeyword, setAiKeyword] = useState('');
  const [aiCategory, setAiCategory] = useState('');
  const [aiCustomInstructions, setAiCustomInstructions] = useState('');
  const [aiScrapeUrls, setAiScrapeUrls] = useState('');
  const [aiGenerateImage, setAiGenerateImage] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [aiModel, setAiModel] = useState('claude-sonnet-4-5');
  const [aiImageModel, setAiImageModel] = useState('nano-banana-2');
  const [aiImageKeywords, setAiImageKeywords] = useState('');

  // Image prompt for manual generation
  const [imagePrompt, setImagePrompt] = useState('');

  useEffect(() => {
    if (isLoggedIn) {
      fetchArticles();
      fetchCategories();
    }
  }, [isLoggedIn]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/categories`);
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const saveCategory = async () => {
    if (!catForm.name.trim()) return;
    try {
      if (editingCat === 'new') {
        const res = await fetch(`${API_URL}/api/blog/categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(catForm),
        });
        if (!res.ok) { const e = await res.json(); alert(e.detail); return; }
      } else {
        const res = await fetch(`${API_URL}/api/blog/categories/${editingCat}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(catForm),
        });
        if (!res.ok) { const e = await res.json(); alert(e.detail); return; }
      }
      setEditingCat(null);
      setCatForm({ name: '', name_en: '' });
      fetchCategories();
    } catch { alert('Fehler beim Speichern'); }
  };

  const deleteCategory = async (slug) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/blog/categories/${slug}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); alert(e.detail); return; }
      fetchCategories();
    } catch { alert('Fehler beim Löschen'); }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/articles`);
      const data = await res.json();
      setArticles(data);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('adminToken', data.token);
        setIsLoggedIn(true);
      } else {
        setLoginError(data.detail || 'Fehler');
      }
    } catch (err) {
      setLoginError('Verbindungsfehler');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
  };

  const handleGenerateAI = async () => {
    if (!aiKeyword || !aiCategory) {
      alert('Bitte Keyword und Kategorie ausfüllen');
      return;
    }
    
    setGenerating(true);
    setGeneratingStatus('Text wird generiert...');
    try {
      const cat = categories.find(c => c.slug === aiCategory);
      const scrapeUrls = aiScrapeUrls.split('\n').map(u => u.trim()).filter(u => u);
      
      // Step 1: Generate text (fast, ~20s)
      const res = await fetch(`${API_URL}/api/blog/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: aiKeyword,
          category: aiCategory,
          category_name: cat?.name || aiCategory,
          language: 'de',
          custom_instructions: aiCustomInstructions || null,
          scrape_urls: scrapeUrls.length > 0 ? scrapeUrls : null,
          generate_image: false, // Never inline - use separate endpoint
          model: aiModel,
        })
      });
      
      const data = await res.json();
      if (data.success && data.article) {
        setFormData({
          ...data.article,
          tags: data.article.tags.join(', ')
        });
        if (data.article.image_prompt) {
          setImagePrompt(data.article.image_prompt);
        }
        setShowEditor(true);
        // Reset AI form
        setAiKeyword('');
        setAiCategory('');
        setAiCustomInstructions('');
        setAiScrapeUrls('');

        // Step 2: Generate image separately if requested
        if (aiGenerateImage && data.article.image_prompt) {
          setGeneratingStatus('Titelbild wird generiert...');
          try {
            const imgRes = await fetch(`${API_URL}/api/blog/generate-image`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                prompt: data.article.image_prompt,
                article_title: data.article.title,
                image_model: aiImageModel,
                image_keywords: aiImageKeywords || null,
              })
            });
            const imgData = await imgRes.json();
            if (imgData.success && imgData.url) {
              setFormData(prev => ({ ...prev, featured_image: imgData.url }));
            } else {
              alert('Artikel erstellt, aber Bildgenerierung fehlgeschlagen: ' + (imgData.detail || 'Unbekannt'));
            }
          } catch (imgErr) {
            console.error('Image generation error:', imgErr);
            alert('Artikel erstellt, aber Bildgenerierung fehlgeschlagen. Bild kann nachträglich generiert werden.');
          }
        }
        setAiGenerateImage(false);
      } else {
        alert('Fehler bei der Generierung: ' + (data.detail || 'Unbekannter Fehler'));
      }
    } catch (err) {
      console.error('AI generation error:', err);
      alert('Fehler bei der KI-Generierung: ' + (err.message || 'Netzwerkfehler oder Timeout'));
    }
    setGenerating(false);
    setGeneratingStatus('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/blog/upload-image`, {
        method: 'POST',
        body: formData
      });
      
      const data = await res.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, featured_image: `${API_URL}${data.url}` }));
      } else {
        alert('Upload fehlgeschlagen');
      }
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + err.message);
    }
    setUploadingImage(false);
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt) {
      alert('Bitte Bildbeschreibung eingeben');
      return;
    }
    
    setGeneratingImage(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: imagePrompt,
          article_title: formData.title
        })
      });
      
      const data = await res.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, featured_image: `${API_URL}${data.url}` }));
      } else {
        alert('Bildgenerierung fehlgeschlagen: ' + (data.detail || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Bildgenerierung fehlgeschlagen: ' + err.message);
    }
    setGeneratingImage(false);
  };

  const handleScrapeUrl = async (url) => {
    if (!url) return;
    setScraping(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const data = await res.json();
      if (data.success) {
        alert(`Inhalt von ${url} geladen (${data.content.length} Zeichen)`);
        return data.content;
      } else {
        alert('Scraping fehlgeschlagen: ' + data.error);
      }
    } catch (err) {
      alert('Scraping fehlgeschlagen');
    }
    setScraping(false);
    return null;
  };

  const handleSaveArticle = async () => {
    try {
      const articleData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      let res;
      if (editingArticle) {
        res = await fetch(`${API_URL}/api/blog/articles/${editingArticle.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      } else {
        res = await fetch(`${API_URL}/api/blog/articles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      }

      if (res.ok) {
        setShowEditor(false);
        setEditingArticle(null);
        resetForm();
        fetchArticles();
      } else {
        const err = await res.json();
        alert('Fehler: ' + (err.detail || 'Speichern fehlgeschlagen'));
      }
    } catch (err) {
      alert('Fehler beim Speichern');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Artikel wirklich löschen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/blog/articles/${id}`, { method: 'DELETE' });
      if (res.ok) fetchArticles();
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setFormData({
      ...article,
      tags: article.tags?.join(', ') || ''
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setFormData({
      title: '', slug: '', meta_description: '', excerpt: '', content: '',
      category: '', category_name: '', tags: '', featured_image: '',
      reading_time: 5, status: 'draft', faq: []
    });
    setImagePrompt('');
  };

  const generateSlug = (title) => {
    let slug = title.toLowerCase();
    slug = slug.replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe').replace(/[üÜ]/g, 'ue').replace(/[ß]/g, 'ss');
    slug = slug.replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').trim();
    return slug;
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <div className={`w-full max-w-md p-8 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-xl'}`}>
          <h1 className={`text-2xl font-bold mb-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Blog Admin
          </h1>
          <form onSubmit={handleLogin}>
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              placeholder="E-Mail"
              className={`w-full px-4 py-3 rounded-lg mb-3 ${
                isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-gray-100 text-gray-900 border-gray-200'
              } border focus:outline-none focus:ring-2 focus:ring-[#7BA7C2]`}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort"
              className={`w-full px-4 py-3 rounded-lg mb-3 ${
                isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-gray-100 text-gray-900 border-gray-200'
              } border focus:outline-none focus:ring-2 focus:ring-[#7BA7C2]`}
            />
            {loginError && <p className="text-red-500 text-sm mb-3">{loginError}</p>}
            <Button type="submit" className="w-full bg-[#7BA7C2] hover:bg-[#5A8BA6] text-white">
              Anmelden
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Editor Screen
  if (showEditor) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <div className="max-w-5xl mx-auto p-6">
          <div className="flex justify-between items-center mb-8">
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {editingArticle ? 'Artikel bearbeiten' : 'Neuer Artikel'}
            </h1>
            <Button variant="outline" onClick={() => { setShowEditor(false); setEditingArticle(null); resetForm(); }}>
              Abbrechen
            </Button>
          </div>

          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Titel *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value, slug: generateSlug(e.target.value) })}
                className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border focus:outline-none focus:ring-2 focus:ring-[#7BA7C2]`}
              />
            </div>

            {/* Slug */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Slug (URL)
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border focus:outline-none focus:ring-2 focus:ring-[#7BA7C2]`}
              />
            </div>

            {/* Featured Image Section */}
            <div className={`p-6 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <label className={`block text-sm font-medium mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Titelbild
              </label>
              
              {formData.featured_image && (
                <div className="mb-4 relative">
                  <img 
                    src={formData.featured_image} 
                    alt="Titelbild" 
                    className="w-full max-h-64 object-cover rounded-lg"
                  />
                  <button 
                    onClick={() => setFormData({...formData, featured_image: ''})}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4">
                {/* URL Input */}
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Bild-URL</label>
                  <input
                    type="url"
                    value={formData.featured_image}
                    onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                    placeholder="https://..."
                    className={`w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                  />
                </div>

                {/* Upload Button */}
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hochladen</label>
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                  <Button 
                    onClick={() => fileInputRef.current?.click()} 
                    disabled={uploadingImage}
                    variant="outline" 
                    className="w-full"
                  >
                    {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    Bild hochladen
                  </Button>
                </div>

                {/* AI Generate Image */}
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>KI generieren</label>
                  <Button 
                    onClick={handleGenerateImage} 
                    disabled={generatingImage || !imagePrompt}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {generatingImage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Image className="w-4 h-4 mr-2" />}
                    Mit Nano Banana
                  </Button>
                </div>
              </div>

              {/* Image Prompt */}
              <div className="mt-4">
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Bildbeschreibung für KI-Generierung
                </label>
                <input
                  type="text"
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder="z.B. Professionelles Foto einer Haartransplantation-Beratung"
                  className={`w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                />
              </div>
            </div>

            {/* Meta Description */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Meta-Description (für SEO)
              </label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                rows={2}
                maxLength={160}
                className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border focus:outline-none focus:ring-2 focus:ring-[#7BA7C2]`}
              />
              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {formData.meta_description.length}/160 Zeichen
              </span>
            </div>

            {/* Excerpt */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Kurzfassung (Excerpt)
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={2}
                className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border focus:outline-none focus:ring-2 focus:ring-[#7BA7C2]`}
              />
            </div>

            {/* Category & Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Kategorie</label>
                <select
                  value={formData.category}
                  onChange={(e) => {
                    const cat = categories.find(c => c.slug === e.target.value);
                    setFormData({ ...formData, category: e.target.value, category_name: cat?.name || '' });
                  }}
                  className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                >
                  <option value="">Kategorie wählen...</option>
                  {categories.map(cat => <option key={cat.slug} value={cat.slug}>{cat.name}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                >
                  <option value="draft">Entwurf</option>
                  <option value="published">Veröffentlicht</option>
                </select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Tags (kommagetrennt)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="haartransplantation, fue, kosten"
                className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
              />
            </div>

            {/* Content */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Inhalt (Markdown)
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={20}
                className={`w-full px-4 py-3 rounded-lg font-mono text-sm ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button onClick={handleSaveArticle} className="bg-[#7BA7C2] hover:bg-[#5A8BA6] text-white">
                {editingArticle ? 'Speichern' : 'Erstellen'}
              </Button>
              {formData.status === 'draft' && (
                <Button 
                  onClick={() => { setFormData({...formData, status: 'published'}); setTimeout(handleSaveArticle, 100); }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Veröffentlichen
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Blog Admin</h1>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/blog')}>
              <Eye className="w-4 h-4 mr-2" />Blog ansehen
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />Abmelden
            </Button>
          </div>
        </div>

        {/* AI Generation - Extended */}
        <div className={`p-6 rounded-xl mb-8 ${isDark ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30' : 'bg-gradient-to-r from-purple-100 to-blue-100'}`}>
          <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Sparkles className="w-5 h-5 text-purple-500" />
            KI-Artikel generieren
          </h2>
          
          {/* Basic Options */}
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <input
                type="text"
                value={aiKeyword}
                onChange={(e) => setAiKeyword(e.target.value)}
                placeholder="Keyword eingeben z.B. 'Haartransplantation Türkei'"
                className={`w-full px-4 py-3 pr-10 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                data-testid="ai-keyword-input"
              />
              <VoiceInput onResult={t => setAiKeyword(prev => prev ? prev + ' ' + t : t)} className="w-7 h-7 absolute right-2 top-1/2 -translate-y-1/2" />
            </div>
            <select
              value={aiCategory}
              onChange={(e) => setAiCategory(e.target.value)}
              className={`px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
              data-testid="ai-category-select"
            >
              <option value="">Kategorie wählen...</option>
              {categories.map(cat => <option key={cat.slug} value={cat.slug}>{cat.name}</option>)}
            </select>
            <Button 
              onClick={handleGenerateAI}
              disabled={generating}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="ai-generate-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{generatingStatus || 'Generiere...'}</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Mit KI generieren</>
              )}
            </Button>
          </div>

          {/* Model Selection Row */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Text-Modell</label>
              <select
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                data-testid="ai-model-select"
              >
                <option value="claude-sonnet-4-5">Claude Sonnet 4.5 (Anthropic)</option>
                <option value="gpt-5.2">GPT-5.2 (OpenAI)</option>
                <option value="gemini-3-flash">Gemini 3 Flash (Google)</option>
                <option value="gemini-3-pro">Gemini 3 Pro (Google)</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Bild-Modell</label>
              <select
                value={aiImageModel}
                onChange={(e) => setAiImageModel(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                data-testid="ai-image-model-select"
              >
                <option value="nano-banana-2">Nano Banana 2 (Gemini 3.1 Flash)</option>
                <option value="nano-banana-pro">Nano Banana Pro (Gemini 3 Pro)</option>
                <option value="gpt-image-1">GPT Image 1 (OpenAI)</option>
              </select>
            </div>
          </div>

          {/* Advanced Options Toggle */}
          <button 
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`text-sm flex items-center gap-1 ${isDark ? 'text-purple-400' : 'text-purple-600'} hover:underline`}
          >
            {showAdvancedOptions ? '▼' : '▶'} Erweiterte Optionen
          </button>

          {/* Advanced Options */}
          {showAdvancedOptions && (
            <div className="mt-4 space-y-4">
              {/* Custom Instructions */}
              <div>
                <label className={`flex items-center gap-1.5 text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Zusätzliche Anweisungen für die KI
                  <VoiceInput onResult={t => setAiCustomInstructions(prev => prev ? prev + ' ' + t : t)} className="w-6 h-6 ml-auto" />
                </label>
                <textarea
                  value={aiCustomInstructions}
                  onChange={(e) => setAiCustomInstructions(e.target.value)}
                  rows={3}
                  placeholder="z.B. 'Fokussiere auf Kostenvergleich Deutschland vs. Türkei', 'Erwähne unsere 5-Jahres-Garantie', 'Schreibe besonders empathisch'"
                  className={`w-full px-4 py-3 rounded-lg ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                />
              </div>

              {/* URLs to Scrape */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Globe className="w-4 h-4 inline mr-1" />
                  URLs für Recherche (eine pro Zeile)
                </label>
                <textarea
                  value={aiScrapeUrls}
                  onChange={(e) => setAiScrapeUrls(e.target.value)}
                  rows={3}
                  placeholder="https://example.com/artikel1&#10;https://example.com/artikel2"
                  className={`w-full px-4 py-3 rounded-lg font-mono text-sm ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Die KI wird diese Seiten als Recherche-Grundlage nutzen (max. 3 URLs)
                </p>
              </div>

              {/* Generate Image Checkbox + Keywords */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiGenerateImage}
                    onChange={(e) => setAiGenerateImage(e.target.checked)}
                    className="w-4 h-4 rounded"
                    data-testid="ai-generate-image-checkbox"
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Image className="w-4 h-4 inline mr-1" />
                    Titelbild automatisch generieren
                  </span>
                </label>
                {aiGenerateImage && (
                  <div>
                    <label className={`flex items-center gap-1.5 text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Bild-Keywords (optional)
                      <VoiceInput onResult={t => setAiImageKeywords(prev => prev ? prev + ' ' + t : t)} className="w-5 h-5 ml-auto" />
                    </label>
                    <input
                      type="text"
                      value={aiImageKeywords}
                      onChange={(e) => setAiImageKeywords(e.target.value)}
                      placeholder="z.B. 'vorher nachher, natürliches Ergebnis, Klinik-Atmosphäre'"
                      className={`w-full px-4 py-2.5 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                      data-testid="ai-image-keywords-input"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Alle Artikel ({articles.length})
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCatManager(o => !o)}
              className={`text-sm ${isDark ? 'text-white border-white/20' : ''}`}
              data-testid="toggle-cat-manager">
              {showCatManager ? 'Kategorien schließen' : 'Kategorien verwalten'}
            </Button>
            <Button onClick={() => setShowEditor(true)} className="bg-[#7BA7C2] hover:bg-[#5A8BA6] text-white">
              <Plus className="w-4 h-4 mr-2" />Neuer Artikel
            </Button>
          </div>
        </div>

        {/* Category Manager */}
        {showCatManager && (
          <div className={`rounded-2xl border p-5 mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`} data-testid="cat-manager">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Kategorien</h3>
              {!editingCat && (
                <Button size="sm" onClick={() => { setEditingCat('new'); setCatForm({ name: '', name_en: '' }); }}
                  className="bg-[#7BA7C2] hover:bg-[#5A8BA6] text-white text-xs" data-testid="add-cat-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Neue Kategorie
                </Button>
              )}
            </div>

            {editingCat && (
              <div className={`rounded-xl border p-4 mb-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Name (DE) *</label>
                    <input
                      value={catForm.name}
                      onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="z.B. Kosten & Finanzierung"
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      data-testid="cat-name-input"
                    />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Name (EN)</label>
                    <input
                      value={catForm.name_en}
                      onChange={e => setCatForm(f => ({ ...f, name_en: e.target.value }))}
                      placeholder="z.B. Costs & Financing"
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      data-testid="cat-name-en-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveCategory} className="bg-[#7BA7C2] hover:bg-[#5A8BA6] text-white text-xs" data-testid="save-cat-btn">
                    Speichern
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingCat(null); setCatForm({ name: '', name_en: '' }); }}
                    className={`text-xs ${isDark ? 'text-white border-white/20' : ''}`}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {categories.map(cat => (
                <div key={cat.slug} className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${isDark ? 'border-white/5 bg-white/3' : 'border-gray-100 bg-gray-50'}`} data-testid={`cat-row-${cat.slug}`}>
                  <div>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{cat.name}</span>
                    {cat.name_en && <span className={`text-xs ml-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>({cat.name_en})</span>}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setEditingCat(cat.slug); setCatForm({ name: cat.name, name_en: cat.name_en || '' }); }}
                      className={`p-1.5 rounded-md transition-colors ${isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-200 text-gray-400'}`}
                      data-testid={`edit-cat-${cat.slug}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteCategory(cat.slug)}
                      className="p-1.5 rounded-md transition-colors text-red-400 hover:bg-red-400/10"
                      data-testid={`delete-cat-${cat.slug}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {categories.length === 0 && (
                <p className={`text-sm text-center py-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Noch keine Kategorien vorhanden.</p>
              )}
            </div>
          </div>
        )}

        {/* Articles List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className={`w-8 h-8 animate-spin mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
        ) : articles.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Noch keine Artikel vorhanden</p>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map(article => (
              <div 
                key={article.id}
                className={`p-4 rounded-xl flex items-center justify-between ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:bg-gray-50 shadow-sm'} transition-colors`}
              >
                <div className="flex items-center gap-4">
                  {article.featured_image && (
                    <img src={article.featured_image} alt="" className="w-16 h-16 object-cover rounded-lg" />
                  )}
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{article.title}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${article.status === 'published' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-600'}`}>
                        {article.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{article.category_name}</span>
                      <span className={`text-sm flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <Clock className="w-3 h-3" />{article.reading_time} min
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => window.open(`/blog/${article.slug}`, '_blank')}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEditArticle(article)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteArticle(article.id)} className="text-red-500 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;
